<template>
  <div>
    <Layout>
      <template #header>
        <div class="mt-8 lg:mt-0 max-w-3xl mx-auto lg:max-w-7xl flex flex-col justify-end">
          <contact-next-prev-buttons @update="getReload" />
          <div class="flex justify-end mt-4">
            <ContactActions style="z-index: 40; margin-top: 30px" :record="contact" @reload="getReload" />
          </div>
        </div>
      </template>
      <div class="mt-8 max-w-3xl mx-auto grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div class="space-y-6 lg:col-start-1 lg:col-span-2">
          <card>
            <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex flex-wrap justify-between">
              <div>
                <h3 class="inline-block text-lg leading-6 font-medium text-gray-900">
                  {{ contact.name }}
                </h3>
              </div>
              <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
                <ContactStageBadge :stage="contact.stage" />
              </p>
            </div>
            <div class="px-4 py-5 sm:px-6" :class="{ 'border-b border-gray-200': customFieldsForEntity.length > 0 }">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Name</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ contact.name }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Email</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <a class="text-indigo-600 hover:text-indigo-900" :href="`mailto:${contact.email}`">{{
                      contact.email
                    }}</a>
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Phone</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <a class="text-indigo-600 hover:text-indigo-900" :href="`tel:${contact.phone}`">{{
                      contact.phone
                    }}</a>
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">RE License Number</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ contact.license }}
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Applied For</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <ul v-if="jobsAppliedTo && jobsAppliedTo.length > 0">
                      <li v-for="job in jobsAppliedTo" :key="job.title">
                        <router-link :to="job.path"> {{ job.title }} - {{ job.city }}, {{ job.state }} </router-link>
                      </li>
                    </ul>
                  </dd>
                </div>
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Lead Source</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <ContactLeadSource :contact="contact" />
                  </dd>
                </div>
              </dl>
            </div>
            <div v-if="customFieldsForEntity.length > 0" class="px-4 py-5 sm:px-6">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div
                  v-for="customField in customFieldsForEntity"
                  :key="customField.id"
                  :class="customField.field_type == 'rich_text' ? 'sm:col-span-2' : 'sm:col-span-1'"
                >
                  <dt class="text-sm leading-5 font-medium text-gray-500">
                    {{ customField.name }}
                  </dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <ValueViewerAndEditor
                      :custom-field="customField"
                      :custom-field-value="contact.customFields.find(f => f.custom_field_id === customField.id)"
                      custom-fieldable-type="Contact"
                      :custom-fieldable-id="contact.id"
                      :method="'both'"
                      :display-submit-button="true"
                      @updated="handleCustomFieldUpdate"
                    />
                  </dd>
                </div>
              </dl>
            </div>
          </card>

          <card
            v-if="Object.keys(contact.customAttributes).length > 0"
            extra-classes="mt-6 overflow-hidden sm:rounded-lg"
          >
            <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex flex-wrap justify-between">
              <div>
                <h3 class="inline-block text-lg leading-6 font-medium text-gray-900">Custom fields</h3>
              </div>
            </div>
            <div class="px-4 py-5 sm:px-6">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-4">
                <div v-for="key in Object.keys(contact.customAttributes)" :key="key" class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">
                    {{ key }}
                  </dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ contact.customAttributes[key] ? contact.customAttributes[key] : 'null' }}
                  </dd>
                </div>
              </dl>
            </div>
          </card>

          <section aria-labelledby="notes-title">
            <ContactInterviews :contact="contact" />
            <ContactNotes
              :contact="contact"
              @activity-created="getNotesAndEmails()"
              @activity-deleted="getNotesAndEmails()"
            />
          </section>
        </div>

        <div>
          <section aria-labelledby="nurture-title" class="lg:col-start-3 lg:col-span-1 mb-6">
            <ContactNurture v-if="contact.stage !== 'Onboarding'" :contact="contact" />
            <ContactOnboardingOverview v-else :contact="contact" />
          </section>
          <section aria-labelledby="enrichment-title">
            <ContactEnrichment v-if="loaded" :contact="contact" />
          </section>
          <section aria-labelledby="timeline-title" class="lg:col-start-3 lg:col-span-1" style="z-index: -1">
            <ContactTimeline :contact="contact" />
          </section>
          <section aria-labelledby="attachments-title" class="mt-6 lg:col-start-3 lg:col-span-1">
            <ContactAttachments :contact="contact" @reload="getReload" />
          </section>
        </div>
      </div>

      <section
        v-if="$route.query.history"
        aria-labelledby="history-title"
        class="mt-6 max-w-3xl mx-auto grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-1"
      >
        <ContactHistory :contact="contact" />
      </section>
    </Layout>
  </div>
</template>

<script>
import Layout from '../Layout/index.vue';
import ContactStageBadge from './ContactStageBadge.vue';
import ContactActions from './ContactActions.vue';
import ContactTimeline from './ContactTimeline.vue';
import ContactAttachments from './ContactAttachments.vue';
import ContactNurture from './ContactNurture.vue';
import ContactNotes from './ContactNotes.vue';
import ContactHistory from './ContactHistory.vue';
import ContactEnrichment from './ContactEnrichment.vue';
import ContactInterviews from './ContactInterviews.vue';
import ContactLeadSource from './ContactLeadSource.vue';
import ContactOnboardingOverview from './ContactOnboardingOverview.vue';
import Card from '../Card/index.vue';
import ContactNextPrevButtons from './ContactNextPrevButtons.vue';
import ValueViewerAndEditor from '../custom_fields/ValueViewerAndEditor.vue';
import DateUtility from '../../utilities/dates';
import { handleCustomFieldUpdate } from '../../utilities/custom_fields';

export default {
  components: {
    Layout,
    Card,
    ContactInterviews,
    ContactStageBadge,
    ContactActions,
    ContactTimeline,
    ContactAttachments,
    ContactNurture,
    ContactNotes,
    ContactHistory,
    ContactEnrichment,
    ContactNextPrevButtons,
    ContactLeadSource,
    ValueViewerAndEditor,
    ContactOnboardingOverview,
  },
  data() {
    return {
      customFieldsForEntity: [],
      contact: {
        name: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        status: '',
        stage: '',
        license: '',
        created: '',
        phoneScreenTime: '',
        interviewTime: '',
        icaAgreementStatus: '',
        nurtureActive: false,
        nurtureAt: null,
        nurtureMessage: '',
        source: '',
        customAttributes: {},
        notesAndEmails: [],
        interviews: [],
        files: [],
        history: [],
        applications: [],
        customFields: [],
      },
      showCustomAttributes: false,
      showChat: false,
      loaded: false,
    };
  },
  computed: {
    jobsAppliedTo() {
      try {
        return this.contact.applications
          .map(application => {
            const location = application.v2_job?.job_locations
              ? application.v2_job.job_locations.find(loc => loc.id === application.job_location_id)
              : {};

            return {
              title: application.v2_job?.title,
              city: location.city,
              state: location.state,
              path: `/jobs/${encodeURIComponent(application.v2_job?.id)}`, // this will be changed to the v2 job id with the client side updayes comming soon
            };
          })
          .filter(job => job !== null);
      } catch (err) {
        console.log('Error getting jobs applied to');
        console.log(err);
        return [];
      }
    },
  },
  mounted() {
    this.getContact();
    this.getNotesAndEmails();
  },
  methods: {
    ...DateUtility,
    handleCustomFieldUpdate(event) {
      handleCustomFieldUpdate(this.contact, event);
    },
    getReload() {
      this.getContact();
      this.getNotesAndEmails();
    },
    getContact() {
      const self = this;
      self.loaded = false;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/contacts/${self.$route.params.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.contact.id = data.record.id;
          self.contact.name = data.record.name;
          self.contact.firstName = data.record.first_name;
          self.contact.lastName = data.record.last_name;
          self.contact.email = data.record.email;
          self.contact.phone = data.record.phone_number;
          self.contact.status = data.record.status;
          self.contact.stage = data.record.stage;
          self.contact.license = data.record.re_license_number;
          self.contact.customAttributes = data.record.jsonb;
          self.contact.created = this.formatDate(data.record.created_at, 'MMMM dd yyyy, h:mm a');
          self.contact.phoneScreenTime = data.record.phone_screen_at
            ? this.formatDate(data.record.phone_screen_at, 'MMMM dd yyyy, h:mm a')
            : null;
          self.contact.interviewTime = data.record.interview_at
            ? this.formatDate(data.record.interview_at, 'MMMM dd yyyy, h:mm a')
            : null;
          self.contact.files = data.files;
          self.contact.icaAgreementStatus = data.record.ica_agreement_status;
          self.contact.nurtureActive = data.record.nurture_active;
          self.contact.nurtureAt = data.record.nurture_at;
          self.contact.nurtureMessage = data.record.nurture_message;
          self.contact.source = data.record.source;
          self.contact.attributionParams = data.record.attribution_params;
          self.contact.applications = data.record.applications;
          self.contact.customFields = data.record.custom_field_values || [];
          self.customFieldsForEntity =
            data.custom_fields_for_entity?.sort((a, b) => a.name.localeCompare(b.name)) || [];
          let contactHistory = [];
          for (let version of data.history) {
            if (version.version.object_changes != null) {
              for (let key of Object.keys(version.version.object_changes)) {
                if (key != 'updated_at') {
                  contactHistory.push({
                    'updated by': version.whodunnit,
                    'field changed': key.replaceAll('_', ' '),
                    from: version.version.object_changes[key][0],
                    to: version.version.object_changes[key][1],
                    'updated at': this.formatDate(version.version.created_at, 'MMMM dd yyyy, h:mm a'),
                  });
                }
              }
            }
          }
          self.contact.history = contactHistory;
          self.loaded = true;
        });
    },
    getStageValue(stageName) {
      let value = {
        Applied: 0,
        'Phone Screen': 1,
        Interview: 2,
        Onboarding: 3,
        Hired: 8,
        Disqualified: 9,
        Nurture: 10,
      };
      return value[stageName];
    },
    getNotesAndEmails() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/contact_notes/${self.$route.params.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.getContactEmails(data);
        });
    },
    getContactEmails(notes) {
      const self = this;
      fetch(`/api/contact_emails/${self.$route.params.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.getEvents(notes, data);
        });
    },
    getEvents(notes, emails) {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/contact_events?contact_id=${self.$route.params.id}`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          let fullTimeline = [];
          for (let note of notes) {
            fullTimeline.push({
              activityType: 'note',
              sortDate: note.created_at,
              ...note,
            });
          }
          for (let email of emails) {
            fullTimeline.push({
              activityType: 'email',
              sortDate: email.created_at,
              ...email,
            });
          }
          fullTimeline.sort((a, b) => {
            return new Date(b.sortDate) - new Date(a.sortDate);
          });
          self.contact.interviews = data;
          self.contact.notesAndEmails = fullTimeline;
        });
    },
  },
};
</script>
